import type { FilterContext } from '@/lib/filters/applyFilters'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import type { SavedFilterScope } from '@/lib/types/savedFilter'
import { parseSavedFilterConfig } from '@/lib/types/savedFilter'
import { taskPassesFilters } from '@/lib/filters/applyFilters'

interface CountableView {
  scope: SavedFilterScope
  scopeEntityId: string | null
  filterConfig: string
}

interface ProjectLike {
  id: string
  parentProjectId: string | null
}

/** Project ids a view reads from: the project, a space and its projects, or everything (null). */
export function viewProjectIds(
  view: Pick<CountableView, 'scope' | 'scopeEntityId'>,
  projects: ReadonlyArray<ProjectLike>,
): Set<string> | null {
  if (view.scope === 'global' || !view.scopeEntityId) return null
  if (view.scope === 'project') return new Set([view.scopeEntityId])
  const ids = new Set([view.scopeEntityId])
  for (const p of projects) if (p.parentProjectId === view.scopeEntityId) ids.add(p.id)
  return ids
}

/**
 * A pinned view's live count: its top-level, unarchived tasks that pass its
 * filters. Closed tasks only count when the view filters on status itself,
 * matching the list, which hides completed work by default.
 */
export function countViewTasks(
  view: CountableView,
  tasks: ReadonlyArray<TaskDocument>,
  projects: ReadonlyArray<ProjectLike>,
  openStatusIds: ReadonlySet<string>,
  context: FilterContext = {},
): number {
  const filters = parseSavedFilterConfig(view.filterConfig)
  const projectIds = viewProjectIds(view, projects)
  const filtersStatus = filters.some((f) => f.fieldKey === 'status' && f.values.length > 0)
  let count = 0
  for (const task of tasks) {
    if (task.isDeleted || task.isArchived || task.parentId) continue
    if (projectIds && (!task.projectId || !projectIds.has(task.projectId))) continue
    if (!filtersStatus && !openStatusIds.has(task.statusId)) continue
    if (!taskPassesFilters(task, filters, context)) continue
    count += 1
  }
  return count
}
